import React, { useState } from 'react';
import { ArrowRight, CheckCircle2, Circle, Loader2, Map as MapIcon, Rocket } from 'lucide-react';
import FrameCard from './FrameCard';
import ProjectOffers from './tabs/ProjectOffers';
import { Card, euros, hours } from './ui';

// Onglet d'un projet en proposition (Rémy, 2026-10-21) : on arrive ici plutôt que sur l'aperçu. On prépare le projet
// — la proposition, le cadre, le budget et la roadmap — puis on lance le kickoff de lancement, une réunion en direct
// où Nexus propose brief, vision et roadmap (2026-10-22) ; à sa clôture le projet passe « En cours », son état de
// départ est enregistré, et il redevient un projet normal.

const Step = ({ done, title, detail, action }) => (
  <li className="flex items-start gap-3 py-2.5">
    {done ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-500" /> : <Circle size={18} className="mt-0.5 shrink-0 text-slate-300" />}
    <div className="min-w-0 flex-1">
      <p className={`text-sm font-semibold ${done ? 'text-slate-700' : 'text-slate-900'}`}>{title}</p>
      {detail && <p className="text-xs text-slate-500">{detail}</p>}
    </div>
    {action}
  </li>
);

const LinkAction = ({ children, onClick }) => (
  <button type="button" onClick={onClick}
    className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50">
    {children} <ArrowRight size={12} />
  </button>
);

const ProposalTab = ({ data, openTab, navigate }) => {
  const { project, iterations, frame, actions, domains } = data;
  const [starting, setStarting] = useState(false);
  const sum = (key) => iterations.reduce((total, it) => total + (Number(it[key]) || 0), 0);
  const budget = { ca: sum('valeur'), hours: sum('budget_time_hours'), purchases: sum('budget_purchase_eur') };
  const frameDone = (frame?.items || []).length > 0;
  const roadmapDone = iterations.length > 0 && budget.hours > 0;

  const startWithoutKickoff = async () => {
    if (!window.confirm('Passer le projet « En cours » sans faire le kickoff dans Deepflow ?')) return;
    setStarting(true);
    try {
      await actions.patchProject({ status: 'in_progress' });
      openTab('apercu');
    } catch {
      // erreur affichée dans le bandeau de la fiche
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-4">
        <Card title="Préparer le projet" icon={Rocket}>
          <p className="text-sm text-slate-600">
            Le projet est en proposition. Au kickoff, quelqu’un le présente à l’équipe qui va le conduire : Nexus écoute
            et propose le brief, la vision et la roadmap, d’après la proposition commerciale et ce qui se dit. À la fin
            du kickoff, il passe <span className="font-semibold">En cours</span> et son état de départ est gardé.
          </p>
          <ol className="mt-2 divide-y divide-slate-100">
            <Step done={frameDone} title="Le cadre du projet"
              detail={frameDone ? `${frame.items.length} puce${frame.items.length > 1 ? 's' : ''}` : 'Règles propres au projet et précisions, en plus des décisions réservées.'} />
            <Step done={roadmapDone} title="Le budget et la roadmap"
              detail={iterations.length
                ? `${iterations.length} itération${iterations.length > 1 ? 's' : ''} · ${hours(budget.hours)} · ${euros(budget.purchases)} d’achats`
                : 'Les itérations, avec leur budget temps et achats : Nexus les proposera au kickoff, d’après la proposition.'}
              action={<LinkAction onClick={() => openTab('roadmap')}>Roadmap</LinkAction>} />
            <Step done={false} title="Le kickoff"
              detail="Réunion en direct avec l’équipe : la fiche se construit devant vous. Le clore passe le projet en cours."
              action={(
                <button type="button" onClick={() => navigate(`/project/${project.id}/kickoff/lancement`)}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
                  <Rocket size={13} /> Lancer le kickoff
                </button>
              )} />
          </ol>
          <button type="button" onClick={startWithoutKickoff} disabled={starting}
            className="mt-1 flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-slate-700 disabled:opacity-50">
            {starting && <Loader2 size={11} className="animate-spin" />} Le kickoff est déjà fait : passer le projet en cours
          </button>
        </Card>
        <FrameCard frame={frame} roadmap={domains?.roadmap} onSave={actions.saveFrame}
          hint="Quelques puces : ce qui vaut pour ce projet (règles en plus des décisions réservées, précisions, limites). Elles seront sur l’aperçu." />
      </div>
      <div className="min-w-0 space-y-4">
        <ProjectOffers projectId={project.id} projectName={project.name} />
        <Card title="Budget prévu" icon={MapIcon} action={<LinkAction onClick={() => openTab('roadmap')}>Roadmap</LinkAction>}>
          {iterations.length ? (
            <div className="grid grid-cols-3 gap-2">
              {[['CA', euros(budget.ca)], ['Temps', hours(budget.hours)], ['Achats', euros(budget.purchases)]].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-slate-50 px-3 py-2">
                  <p className="text-[11px] text-slate-500">{label}</p>
                  <p className="text-sm font-semibold tabular-nums text-slate-800">{value}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Pas encore d’itération : la roadmap porte le budget temps et achats de chaque itération.</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ProposalTab;
